import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorite, invalidateFavorites } from "@/hooks/useFavorite";

/**
 * Heart-toggle icon that persists favorites to /api/favorites.
 * kind: "provider" | "service"; target_id: DB id of the entity being saved.
 */
export default function FavoriteButton({ kind = "provider", targetId, initial = false, size = "md", onChange }) {
  const { user } = useAuth();
  const [saved, setSaved] = useFavorite(kind, targetId);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggle = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      // Login-gated: redirect to /login with next=<current path> so the user
      // returns here after signing in.
      const next = encodeURIComponent(location.pathname + location.search);
      toast("Sign in to save favorites", { description: "Redirecting…" });
      navigate(`/login?next=${next}`);
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/favorites/target/${kind}/${targetId}`);
        setSaved(false);
        onChange?.(false);
      } else {
        await api.post("/favorites", { kind, target_id: targetId });
        setSaved(true);
        onChange?.(true);
        toast.success("Saved to your favorites");
      }
      invalidateFavorites();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not update favorite");
    } finally {
      setBusy(false);
    }
  };

  const dim = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-5 w-5" : "h-4 w-4";
  const btn = size === "lg" ? "h-10 w-10" : "h-8 w-8";
  return (
    <button
      type="button"
      aria-label={saved ? "Remove from favorites" : "Save to favorites"}
      onClick={toggle}
      disabled={busy}
      data-testid={`fav-toggle-${kind}-${targetId}`}
      className={`inline-flex items-center justify-center rounded-full ${btn} border border-slate-200 bg-white/95 hover:border-[#165a71]/50 hover:bg-white transition-colors backdrop-blur ${saved ? "text-rose-500" : "text-[#252A34]/60"}`}
    >
      <Heart className={`${dim} ${saved ? "fill-rose-500" : ""}`} />
    </button>
  );
}
